import React, { useState, useEffect } from 'react';
import { DepositTable } from '../../components/bmp/DepositTable';
import { MonthlyInvestmentTable } from '../../components/bmp/MonthlyInvestmentTable';
import { InvestmentTable } from '../../components/bmp/InvestmentTable';
import { CostDeductionTable } from '../../components/bmp/CostDeductionTable';
import { WalletIncreasementTable } from '../../components/bmp/WalletIncreasementTable';
import AuditTrailTable from '../../components/bmp/AuditTrailTable';
import { getBMPAutomaticAlerts } from '../../api/getBMPAlerts';
import { getBybitPricesData, BybitPricesResponse } from '../../api/getBybitPricesData';
import { BMP_AutomaticAlert } from '../../models/bmp.model';
import { AlertHandlerModal } from '../../components/bmp/modals/AlertHandlerModal';
import { toast } from 'react-toastify';
import { responseHero } from '../../utils/axiosInterceptor';

export const BMPDashboard = () => {
    const [activeOption, setActiveOption] = useState<string | null>(null);
    const [automaticAlerts, setAutomaticAlerts] = useState<BMP_AutomaticAlert[]>([]);
    
    // Árfolyamok és frissítési adatok állapotai
    const [prices, setPrices] = useState<BybitPricesResponse | null>(null);
    const [pricesLastUpdated, setPricesLastUpdated] = useState<Date | null>(null);
    const [isPricesLoading, setIsPricesLoading] = useState<boolean>(false);

    // Modal állapotkezelése
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedAlert, setSelectedAlert] = useState<BMP_AutomaticAlert | null>(null);

    // Function to return to the menu (passed to children)
    const handleBack = () => {
        setActiveOption(null);
    };

    // Riasztások betöltése függvény
    const fetchAlerts = async () => {
        const res = await getBMPAutomaticAlerts();
        setAutomaticAlerts(res);
    };

    // Árfolyamok betöltése függvény
    const fetchPrices = async () => {
        setIsPricesLoading(true);
        try {
            const res = await getBybitPricesData();
            setPrices(res);
            setPricesLastUpdated(new Date());
        } catch (error) {
            toast.error("Hiba történt a Bybit árfolyamok lekérése során.");
        } finally {
            setIsPricesLoading(false);
        }
    };

    useEffect(() => {
        fetchAlerts();
        fetchPrices();
    }, []);

    // Riasztás gombra kattintás kezelője
    const handleOpenAlertModal = (alert: BMP_AutomaticAlert) => {
        setSelectedAlert(alert);
        setIsModalOpen(true);
    };

    // Modal bezárása mentés nélkül
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedAlert(null);
    };

    // Sikeres leokézás kezelője
    const handleModalSuccess = async () => {
        setIsModalOpen(false);
        setSelectedAlert(null);
        await fetchAlerts(); 
    };

    const handleDeleteClick = async (id: string) => {
        const isConfirmed = window.confirm(`Biztosan törölni szeretnéd ezt a riasztást? (ID: ${id})`);
        if (!isConfirmed) return;

        try {
            await responseHero.delete(`${process.env.REACT_APP_API_URL}/bmp/automaticalerts/${id}`);
            toast.success("Sikeres törlés!");
            await fetchAlerts(); 
        } catch (error: any) {
            toast.error(error?.response?.data?.message || "Hiba történt a törlés során.");
        }
    };

    // Segédfüggvény az alert aktuális státuszának (színének) meghatározásához
    const getAlertStatusColor = (alert: BMP_AutomaticAlert): 'green' | 'red' | 'neutral' => {
        if (!prices) return 'neutral';
        
        const assetName = (alert.asset?.name || '').toLowerCase();
        const isBtc = assetName.includes('btc') || assetName.includes('bitcoin');
        const isEth = assetName.includes('eth') || assetName.includes('ethereum');
        
        const currentPrice = isBtc 
            ? (prices.btc ? Number(prices.btc) : null) 
            : isEth 
                ? (prices.eth ? Number(prices.eth) : null) 
                : null;
    
        if (currentPrice === null || !alert.exchangeRate) return 'neutral';
    
        const targetPrice = Number(alert.exchangeRate);
    
        // operation === true (Buy):  Green if targetPrice <= currentPrice
        // operation === false (Sell): Green if targetPrice >= currentPrice
        const isTargetMet = alert.operation 
            ? targetPrice <= currentPrice
            : targetPrice >= currentPrice;
    
        return isTargetMet ? 'green' : 'red';
    };

    // Riasztások rendezése és szűrése az operation flag alapján
    const getSortedAlerts = (isBuyingAlert: boolean) => {
        return [...automaticAlerts]
            .filter(alert => alert.operation === isBuyingAlert)
            .sort((a, b) => {
                const colorA = getAlertStatusColor(a);
                const colorB = getAlertStatusColor(b);

                const weight = { green: 1, red: 2, neutral: 3 };

                if (weight[colorA] !== weight[colorB]) {
                    return weight[colorA] - weight[colorB];
                }

                return new Date(b.date).getTime() - new Date(a.date).getTime();
            });
    };

    // Split alerts into two filtered lists
    const realizationAlerts = getSortedAlerts(false); // operation = false
    const buyingAlerts = getSortedAlerts(true);       // operation = true

    // Updated menu options
    const menuOptions = [
        {
            id: 'payment_register',
            title: "Befizetési nyilvántartó",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
            ),
            component: <InvestmentTable />
        },
        {
            id: 'monthly_register',
            title: "Havi befizetés",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
            ),
            component: <MonthlyInvestmentTable />
        },
        {
            id: 'investment_register',
            title: "Befektetési nyilvántartó",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
            ),
            component: <DepositTable />
        },
        {
            id: 'wallet_increase_register',
            title: "Befektetés összesítések",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
            ),
            component: <WalletIncreasementTable />
        },
        {
            id: 'cost_deduction_register',
            title: "Költség nyilvántartó",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M14.121 14.121L19 19m-7-7l7-7m-7 7l-2.879 2.879M12 12L9.121 9.121m0 5.758a3 3 0 11-4.243 4.243 3 3 0 014.243-4.243zm0-5.758a3 3 0 11-4.243-4.243 3 3 0 014.243 4.243z" />
                </svg>
            ),
            component: <CostDeductionTable />
        },
        {
            id: 'audit_register',
            title: "Audit nyilvántartó",
            icon: (
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            ),
            component: <AuditTrailTable />
        }
    ];

    const activeContent = menuOptions.find(o => o.id === activeOption);

    // Reusable Column Component with context awareness for Realization vs Buying
    const renderAlertColumn = (title: string, alertsList: BMP_AutomaticAlert[], isBuyingColumn: boolean) => (
        <div className="w-full lg:w-1/2">
            <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                    {title}
                </h2>
                <div className="flex gap-2">
                    <span className={`${isBuyingColumn ? 'bg-blue-100 text-blue-800' : 'bg-amber-100 text-amber-800'} py-1 px-3 rounded-full text-xs font-bold`}>
                        {alertsList.length} db
                    </span>
                </div>
            </div>
            
            <div className="space-y-3 max-h-[700px] overflow-y-auto pr-2 custom-scrollbar">
                {alertsList.length === 0 ? (
                    <div className="bg-white rounded-xl p-8 text-center border border-gray-100">
                        <p className="text-sm text-gray-400">Jelenleg nincs riasztás.</p>
                    </div>
                ) : (
                    alertsList.map((alert) => {
                        const statusColor = getAlertStatusColor(alert);
                        
                        let cardBackgroundClass = "bg-white border-gray-100 shadow-sm";
                        if (statusColor === 'green') {
                            cardBackgroundClass = "bg-emerald-50/60 border-emerald-200 shadow-sm shadow-emerald-50";
                        } else if (statusColor === 'red') {
                            cardBackgroundClass = "bg-rose-50/60 border-rose-200 shadow-sm shadow-rose-50";
                        }

                        // Determine strategy label contextually
                        const strategyLabel = isBuyingColumn 
                            ? alert.alertBuyingLevel?.replace('_', ' ') || '-'
                            : alert.alertRealizationLevel?.replace('_', ' ') || '-';

                        return (
                            <div 
                                key={alert.id} 
                                className={`${cardBackgroundClass} border rounded-xl p-5 hover:shadow-md transition-all duration-200 flex flex-col justify-between`}
                            >
                                {/* Felső sáv */}
                                <div className="flex justify-between items-start pb-3 border-b border-gray-100 mb-4">
                                    <div>
                                        <span className="text-xs font-semibold tracking-wider text-gray-400 uppercase">Csoport azonosító</span>
                                        <h4 className="text-xl font-bold text-gray-900 tracking-tight mt-0.5">
                                            #{alert.depositGroup_id}
                                        </h4>
                                    </div>
                                    <div className="text-right">
                                        <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-md mb-1 border ${
                                            isBuyingColumn 
                                                ? 'text-blue-600 bg-blue-50/60 border-blue-100/50' 
                                                : 'text-amber-600 bg-amber-50/60 border-amber-100/50'
                                        }`}>
                                            {alert.automaticAlertStatus}
                                        </span>
                                        <p className="text-xs text-gray-400 font-normal">
                                            {new Date(alert.date).toLocaleString('hu-HU', { hour: '2-digit', minute: '2-digit', month: 'short', day: 'numeric' })}
                                        </p>
                                    </div>
                                </div>

                                {/* Középső rész */}
                                <div className="mb-5">
                                    <div className="flex justify-between items-baseline mb-3">
                                        <span className="text-sm font-semibold text-gray-800">
                                            {alert.asset?.name || `Eszköz ID: ${alert.assetType}`}
                                        </span>
                                        <span className="text-xs font-medium text-gray-400 bg-white/80 px-2 py-0.5 rounded text-center border border-gray-100">
                                            {strategyLabel}
                                        </span>
                                    </div>

                                    {/* Adat rács */}
                                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs pt-1">
                                        <div className="flex justify-between border-b border-gray-100 pb-1.5">
                                            <span className="text-gray-400">Célárfolyam</span>
                                            <span className="font-bold text-gray-700">
                                                {alert.exchangeRate ? `${alert.exchangeRate.toString()} USD` : '-'}
                                            </span>
                                        </div>
                                        <div className="flex justify-between border-b border-gray-100 pb-1.5">
                                            {isBuyingColumn ? (
                                                <>
                                                    <span className="text-gray-400">Összeg</span>
                                                    <span className="font-medium text-gray-700">
                                                        {alert.amount ? `${alert.amount.toString()} USD` : '-'}
                                                    </span>
                                                </>
                                            ) : (
                                                <>
                                                    <span className="text-gray-400">Mennyiség</span>
                                                    <span className="font-medium text-gray-700">
                                                        {alert.quantity ? alert.quantity.toString() : '-'}
                                                    </span>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Alsó rész */}
                                <div className="pt-3 flex gap-3 w-full">
                                    <div className="w-1/2">
                                        <button 
                                            className={`w-full text-center text-white text-xs font-semibold py-2.5 px-4 rounded-xl shadow-sm transition-all duration-200 tracking-wide focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                                                isBuyingColumn 
                                                    ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800 focus:ring-blue-500' 
                                                    : 'bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 focus:ring-emerald-500'
                                            }`}
                                            onClick={() => handleOpenAlertModal(alert)}
                                        >
                                            {isBuyingColumn ? 'Vásárlás' : 'Realizálás'}
                                        </button>
                                    </div>
                                    <div className="w-1/2">
                                        <button 
                                            className="w-full text-center bg-white/80 hover:bg-red-50 text-slate-600 hover:text-red-600 border border-slate-200 hover:border-red-200 text-xs font-semibold py-2.5 px-4 rounded-xl transition-all duration-200 tracking-wide focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1"
                                            onClick={() => handleDeleteClick(alert.id)}
                                        >
                                            Törlés
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );

    return (
        <div className="min-h-screen px-6 py-10 bg-gray-50">
            {/* Header */}
            {!activeOption && (
                <div className="max-w-7xl mx-auto mb-6 text-center animate-fade-in-down">
                    <h1 className="text-4xl font-extrabold text-amber-950 mb-4 tracking-tight">
                        Bitcoin megtakarítási program
                    </h1>
                    <p className="text-gray-500 text-lg">Adminisztrációs felület</p>
                    <div className="h-1 w-24 bg-amber-700 mx-auto mt-4 mb-4 rounded-full"></div>
                </div>
            )}

            {/* Bybit Live Prices */}
            {!activeOption && (
                <div className="max-w-7xl mx-auto mb-6 animate-fade-in-down">
                    <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm flex flex-col sm:flex-row items-center justify-between gap-4">
                        <div className="flex flex-wrap items-center gap-6">
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-orange-500 animate-pulse"></span>
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">BTC:</span>
                                <span className="text-lg font-mono font-extrabold text-gray-900">
                                    {prices?.btc ? `${Number(prices.btc).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD` : 'Betöltés...'}
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500 animate-pulse"></span>
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">ETH:</span>
                                <span className="text-lg font-mono font-extrabold text-gray-900">
                                    {prices?.eth ? `${Number(prices.eth).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD` : 'Betöltés...'}
                                </span>
                            </div>
                        </div>
                        
                        <div className="flex items-center gap-3 self-end sm:self-center text-xs text-gray-500">
                            <span>
                                Utoljára frissítve:{' '}
                                <strong className="text-gray-700">
                                    {pricesLastUpdated 
                                        ? pricesLastUpdated.toLocaleTimeString('hu-HU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }) 
                                        : '-'}
                                </strong>
                            </span>
                            <button
                                onClick={() => {
                                    fetchAlerts()
                                    fetchPrices()
                                }}
                                disabled={isPricesLoading}
                                className={`flex items-center justify-center p-2 rounded-lg border border-gray-200 bg-gray-50 hover:bg-amber-50 hover:border-amber-300 hover:text-amber-800 transition-all ${isPricesLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                title="Árfolyamok frissítése"
                            >
                                <svg 
                                    xmlns="http://www.w3.org/2000/svg" 
                                    className={`h-4 w-4 ${isPricesLoading ? 'animate-spin text-amber-700' : 'text-gray-600'}`} 
                                    fill="none" 
                                    viewBox="0 0 24 24" 
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.253 8H18" />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Compact Top Menu */}
            {!activeOption && (
                <div className="max-w-7xl mx-auto mb-10 animate-fade-in-up">
                    <div className="flex flex-wrap items-center justify-center gap-3">
                        {menuOptions.map((option) => (
                            <button 
                                key={option.id}
                                onClick={() => setActiveOption(option.id)}
                                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md hover:border-amber-400 hover:text-amber-800 transition-all duration-200 text-sm font-semibold text-gray-700"
                            >
                                <span className="text-amber-600">
                                    {option.icon}
                                </span>
                                {option.title}
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Main Split Layout - Realization left, Buying right */}
            {!activeOption && (
                <div className="max-w-7xl mx-auto flex flex-col lg:flex-row gap-10 animate-fade-in-up">
                    {renderAlertColumn("Aktív vásárlási riasztások", buyingAlerts, true)}
                    {renderAlertColumn("Aktív eladási riasztások", realizationAlerts, false)}
                </div>
            )}

            {/* Active Component Display */}
            {activeOption && activeContent && (
                <div className="max-w-7xl mx-auto animate-fade-in">
                    {React.cloneElement(activeContent.component as React.ReactElement, { 
                        onBack: handleBack 
                    })}
                </div>
            )}

            {/* FELDOLGOZÓ MODAL INTEGRÁCIÓ */}
            <AlertHandlerModal 
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                alertData={selectedAlert}
                onSuccess={handleModalSuccess}
            />
        </div>
    );
}